import { Injectable } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { CollectionPaths } from '@models/collections';
import {
	GlobalEmailTemplate,
	GlobalEmailTemplateMetadata,
} from '@models/global-email-templates';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { BusinessEmailTemplateVersionsRepo } from '@repos/email-templates/business-email-templates/business-email-template-versions.repo';
import { BusinessEmailTemplatesRepo } from '@repos/email-templates/business-email-templates/business-email-templates.repo';
import { AuthState } from '@state/auth/auth.state';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { FirestoreStateBaseForBusinessWithSubscription } from '@state/FirestoreStateBaseForBusiness';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import * as actions from './business-email-templates.actions';
import { BusinessEmailTemplatesStateModel } from './business-email-templates.models';

const DEFAULT_STATE: BusinessEmailTemplatesStateModel = {
	...DEFAULT_STATE_BASE,
	templates: [],
	templateVersions: {},
	error: null,
};

const defaultTemplateVersion: GlobalEmailTemplate = {
	subject: '',
	htmlMessage: '',
	attachments: [],
	createdBy: '',
	createdAt: 0,
};

@State<BusinessEmailTemplatesStateModel>({
	name: 'businessEmailTemplates',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class BusinessEmailTemplatesState extends FirestoreStateBaseForBusinessWithSubscription<
	GlobalEmailTemplateMetadata,
	BusinessEmailTemplatesStateModel
> {
	@SelectSnapshot(AuthState.getUserId) private userId: string;

	constructor(
		protected store: Store,
		private businessEmailTemplatesRepo: BusinessEmailTemplatesRepo,
		private businessEmailTemplateVersionsRepo: BusinessEmailTemplateVersionsRepo,
	) {
		super(
			() => CollectionPaths.business_email_templates(this.businessId),
			(payload) => ({ templates: payload as GlobalEmailTemplateMetadata[] }),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.SelectBusinessEmailTemplate)
	async selectTemplate(
		ctx: StateContext<BusinessEmailTemplatesStateModel>,
		{ templateId }: actions.SelectBusinessEmailTemplate,
	) {
		ctx.setState(
			patch<BusinessEmailTemplatesStateModel>({
				error: null,
			}),
		);

		const state = ctx.getState();
		const template = state.templates.find((t) => t.id === templateId);

		if (template?.versionId) {
			try {
				const versions = await lastValueFrom(
					this.businessEmailTemplateVersionsRepo
						.All({ templateId })
						.pipe(take(1)),
				);
				if (versions) {
					ctx.setState(
						patch<BusinessEmailTemplatesStateModel>({
							templateVersions: patch({
								[templateId]: Object.fromEntries(
									versions.map((version) => [version.id, version]),
								),
							}),
						}),
					);
				}
			} catch (error) {
				ctx.setState(
					patch<BusinessEmailTemplatesStateModel>({
						error:
							error instanceof Error
								? error.message
								: 'Failed to load template version',
					}),
				);
			}
		}
	}

	@Action(actions.CreateBusinessEmailTemplate)
	async createTemplate(
		ctx: StateContext<BusinessEmailTemplatesStateModel>,
		{ template, afterCreateAction }: actions.CreateBusinessEmailTemplate,
	) {
		let createdTemplate: DocumentReference<GlobalEmailTemplateMetadata>;

		ctx.setState(
			patch<BusinessEmailTemplatesStateModel>({
				error: null,
			}),
		);

		try {
			createdTemplate = await this.repo.Add(template);
			const templateVersion =
				await this.businessEmailTemplateVersionsRepo.Create(
					{
						subject: '',
						htmlMessage: '',
						attachments: [],
						createdBy: this.userId,
						createdAt: Date.now(),
					},
					{ templateId: createdTemplate.id },
				);
			if (templateVersion) {
				await this.repo.CreateOrMerge(createdTemplate.id, {
					...template,
					versionId: templateVersion.id,
				});
			}
			if (afterCreateAction) {
				afterCreateAction(createdTemplate.id);
			}
		} catch (error) {
			ctx.setState(
				patch<BusinessEmailTemplatesStateModel>({
					error:
						error instanceof Error ? error.message : 'Unknown error occurred',
				}),
			);
			throw error;
		}
	}

	@Action(actions.UpdateBusinessEmailTemplate)
	async updateTemplate(
		ctx: StateContext<BusinessEmailTemplatesStateModel>,
		{
			templateId,
			templateMetadata,
			templateVersion,
		}: actions.UpdateBusinessEmailTemplate,
	) {
		try {
			const templateVersionData = {
				...defaultTemplateVersion,
				...templateVersion,
				createdBy: this.userId,
				createdAt: Date.now(),
			};
			const templateVersionDataRef =
				await this.businessEmailTemplateVersionsRepo.Create(
					templateVersionData,
					{ templateId },
				);

			if (templateVersionDataRef) {
				await this.repo.CreateOrMerge(templateId, {
					...templateMetadata,
					versionId: templateVersionDataRef.id,
				});

				ctx.setState(
					patch<BusinessEmailTemplatesStateModel>({
						templates: updateItem<GlobalEmailTemplateMetadata>(
							(template) => template.id === templateId,
							patch({
								...templateMetadata,
								versionId: templateVersionDataRef.id,
							}),
						),
						templateVersions: patch({
							[templateId]: patch({
								[templateVersionDataRef.id]: templateVersionData,
							}),
						}),
					}),
				);
			} else {
				throw new Error('Failed to create template version');
			}
		} catch (error) {
			ctx.setState(
				patch<BusinessEmailTemplatesStateModel>({
					error:
						error instanceof Error ? error.message : 'Unknown error occurred',
				}),
			);
			throw error;
		}
	}

	@Action(actions.DeleteBusinessEmailTemplate)
	async deleteTemplate(
		ctx: StateContext<BusinessEmailTemplatesStateModel>,
		{ id }: actions.DeleteBusinessEmailTemplate,
	) {
		const versions = await lastValueFrom(
			this.businessEmailTemplateVersionsRepo
				.All({ templateId: id })
				.pipe(take(1)),
		);
		await Promise.all(
			versions.map((version) =>
				this.businessEmailTemplateVersionsRepo.Delete(version.id, {
					templateId: id,
				}),
			),
		);
		await this.repo.Delete(id);
		ctx.setState(
			patch<BusinessEmailTemplatesStateModel>({
				templates: (templates) => templates.filter((t) => t.id !== id),
				templateVersions: (versions) =>
					Object.fromEntries(
						Object.entries(versions).filter(([key]) => key !== id),
					),
			}),
		);
	}

	@Action(actions.LoadBusinessEmailTemplateVersions)
	async loadTemplateVersion(
		ctx: StateContext<BusinessEmailTemplatesStateModel>,
		{ templateId }: actions.LoadBusinessEmailTemplateVersions,
	) {
		const state = ctx.getState();
		const template = state.templates.find((t) => t.id === templateId);

		if (template?.versionId) {
			try {
				const versions = await lastValueFrom(
					this.businessEmailTemplateVersionsRepo
						.All({ templateId })
						.pipe(take(1)),
				);
				if (versions) {
					ctx.setState(
						patch<BusinessEmailTemplatesStateModel>({
							templateVersions: patch({
								[templateId]: Object.fromEntries(
									versions.map((version) => [version.id, version]),
								),
							}),
						}),
					);
				}
			} catch (error) {
				ctx.setState(
					patch<BusinessEmailTemplatesStateModel>({
						error:
							error instanceof Error
								? error.message
								: 'Failed to load template version',
					}),
				);
			}
		}
	}
}
